// Adapted from custom-report UI 17d8d0d35, utils/dashboardEditor.js.
// Numeric lexemes and quoted fields stay intact while switching editor modes.
export const KPI_AGGREGATES = ["sum", "avg", "min", "max", "count"]
export const KPI_ARITY = { safe_divide: [2], round: [1, 2], abs: [1], if_else: [3] }
export const KPI_COMPARISONS = [">", ">=", "<", "<=", "==", "!="]
const NUMBER = /^(?:0[xX][\da-fA-F](?:_?[\da-fA-F])*|0[bB][01](?:_?[01])*|0[oO][0-7](?:_?[0-7])*|(?:\d(?:_?\d)*(?:\.(?:\d(?:_?\d)*)?)?|\.\d(?:_?\d)*)(?:[eE][+-]?\d(?:_?\d)*)?)/

export class KpiFormulaError extends Error {
  constructor(code, offset = 0) { super(code); this.code = code; this.offset = offset }
}

export function parseKpiFormula(expression) {
  if (typeof expression !== "string" || !expression.trim()) throw new KpiFormulaError("empty")
  if (expression.length > 1000) throw new KpiFormulaError("length")
  const tokens = []
  let offset = 0, parentheses = 0, position = 0, tokenizing = true
  const fail = (code) => { throw new KpiFormulaError(code, tokenizing ? offset : tokens[position]?.offset ?? offset) }
  while (offset < expression.length) {
    const rest = expression.slice(offset)
    if (/^\s/.test(rest)) {
      if (rest[0] === "\n" && !parentheses && tokens.length && rest.replace(/#[^\n]*/g, "").trim()) fail("multiline")
      offset++; continue
    }
    if (rest[0] === "#") { const end = rest.indexOf("\n"); offset += end < 0 ? rest.length : end; continue }
    if (rest.startsWith("\\\n") || rest.startsWith("\\\r\n")) { offset += rest.startsWith("\\\r") ? 3 : 2; continue }
    let kind = "symbol", value
    if (rest[0] === "`") {
      const end = rest.indexOf("`", 1)
      if (end < 1 || !rest.slice(1, end).trim()) fail("field")
      value = rest.slice(0, end + 1); kind = "field"
    } else if (NUMBER.test(rest)) {
      value = rest.match(NUMBER)[0]
      if (/^0[\d_]+$/.test(value) && /[1-9]/.test(value) || !Number.isFinite(Number(value.replace(/_/g, "")))) fail("number")
      kind = "number"
    } else {
      const name = rest.match(/^[\p{L}_][\p{L}\p{N}\p{M}_]*/u)
      if (name) { value = name[0]; kind = "name" }
      else value = rest.match(/^(?:>=|<=|==|!=|[()+\-*/<>,])/)?.[0]
    }
    if (!value) fail("syntax")
    if (value === "(" && ++parentheses > 100) fail("complexity")
    if (value === ")") parentheses--
    tokens.push({ kind, value, offset })
    if (tokens.length > 300) fail("complexity")
    offset += value.length
  }
  tokenizing = false
  const peek = () => tokens[position]?.value
  const consume = (value) => { if (peek() !== value) fail("syntax"); return tokens[position++] }
  function primary() {
    if (peek() === "(") { consume("("); const result = logicalOr(); consume(")"); return result }
    const token = tokens[position++]
    if (!token) fail("syntax")
    if (token.kind === "number") return { type: "number", value: token.value }
    if (["True", "False", "None"].includes(token.value)) return { type: "literal", value: token.value }
    if (token.kind !== "name" || peek() !== "(") fail("aggregateRequired")
    const name = token.value, method = name.toLowerCase()
    consume("(")
    if (KPI_AGGREGATES.includes(method)) {
      if (method === "count") { consume(")"); return { type: "aggregate", name, column: null, quoted: false } }
      const field = tokens[position++]
      if (!field || !["field", "name"].includes(field.kind) || ["and", "or", "not", "True", "False", "None"].includes(field.value)) fail("field")
      if (peek() === ",") consume(",")
      consume(")")
      return { type: "aggregate", name, column: field.kind === "field" ? field.value.slice(1, -1) : field.value, quoted: field.kind === "field" }
    }
    if (!Object.hasOwn(KPI_ARITY, method)) fail("function")
    const args = []
    if (peek() !== ")") {
      args.push(logicalOr())
      while (peek() === ",") { consume(","); if (peek() === ")") break; args.push(logicalOr()) }
    }
    consume(")")
    if (!KPI_ARITY[method].includes(args.length)) fail("arguments")
    return { type: "call", name, args }
  }
  function unary(depth = 0) {
    if (depth > 20) fail("complexity")
    if (["+", "-"].includes(peek())) return { type: "unary", operator: tokens[position++].value, operand: unary(depth + 1) }
    return primary()
  }
  function product() {
    let left = unary()
    while (["*", "/"].includes(peek())) left = { type: "binary", operator: tokens[position++].value, left, right: unary() }
    return left
  }
  function sum() {
    let left = product()
    while (["+", "-"].includes(peek())) left = { type: "binary", operator: tokens[position++].value, left, right: product() }
    return left
  }
  function comparison() {
    let left = sum()
    if (KPI_COMPARISONS.includes(peek())) left = { type: "binary", operator: tokens[position++].value, left, right: sum() }
    if (KPI_COMPARISONS.includes(peek())) fail("chainedComparison")
    return left
  }
  function logicalNot(depth = 0) {
    if (depth > 20) fail("complexity")
    if (peek() === "not") { consume("not"); return { type: "unary", operator: "not", operand: logicalNot(depth + 1) } }
    return comparison()
  }
  function logicalAnd() {
    const values = [logicalNot()]
    while (peek() === "and") { consume("and"); values.push(logicalNot()) }
    return values.length === 1 ? values[0] : { type: "logic", operator: "and", values }
  }
  function logicalOr() {
    const values = [logicalAnd()]
    while (peek() === "or") { consume("or"); values.push(logicalAnd()) }
    return values.length === 1 ? values[0] : { type: "logic", operator: "or", values }
  }
  const tree = logicalOr()
  if (position !== tokens.length) fail("syntax")
  function metrics(node) {
    if (["number", "literal"].includes(node.type)) return { count: 1, depth: 1 }
    if (node.type === "aggregate") return { count: node.column === null ? 3 : 5, depth: 3 }
    const children = node.type === "binary" ? [node.left, node.right] : node.type === "unary" ? [node.operand] : node.args || node.values
    const values = children.map(metrics)
    return { count: (node.type === "call" ? 3 : 2) + values.reduce((sum, value) => sum + value.count, 0), depth: 1 + Math.max(node.type === "call" ? 2 : 1, ...values.map((value) => value.depth)) }
  }
  const complexity = metrics(tree)
  if (complexity.count + 1 > 100 || complexity.depth + 1 > 20) fail("complexity")
  return tree
}

export function serializeKpiFormula(tree) {
  function write(node, depth = 0) {
    if (!node || depth > 20) throw new KpiFormulaError("complexity")
    const child = (value) => write(value, depth + 1)
    switch (node.type) {
      case "number": {
        const value = typeof node.value === "string" ? node.value.replace(/^[+-]/, "") : ""
        if (!value || NUMBER.exec(value)?.[0] !== value) throw new KpiFormulaError("number")
        return node.value
      }
      case "literal":
        if (!["True", "False", "None"].includes(node.value)) throw new KpiFormulaError("syntax")
        return node.value
      case "aggregate":
        if (!KPI_AGGREGATES.includes(node.name?.toLowerCase())) throw new KpiFormulaError("function")
        if (node.name.toLowerCase() === "count") return `${node.name}()`
        if (typeof node.column !== "string" || !node.column.trim() || node.column.includes("`")) throw new KpiFormulaError("field")
        if (!node.quoted && !/^[\p{L}_][\p{L}\p{N}\p{M}_]*$/u.test(node.column)) throw new KpiFormulaError("field")
        return `${node.name}(${node.quoted ? `\`${node.column}\`` : node.column})`
      case "binary":
        if (!["+", "-", "*", "/", ...KPI_COMPARISONS].includes(node.operator)) throw new KpiFormulaError("syntax")
        return `(${child(node.left)} ${node.operator} ${child(node.right)})`
      case "unary":
        if (!["+", "-", "not"].includes(node.operator)) throw new KpiFormulaError("syntax")
        return `(${node.operator} ${child(node.operand)})`
      case "logic":
        if (!["and", "or"].includes(node.operator) || !Array.isArray(node.values) || node.values.length < 2) throw new KpiFormulaError("syntax")
        return `(${node.values.map(child).join(` ${node.operator} `)})`
      case "call":
        if (!Object.hasOwn(KPI_ARITY, node.name?.toLowerCase()) || !KPI_ARITY[node.name.toLowerCase()].includes(node.args?.length)) throw new KpiFormulaError("arguments")
        return `${node.name}(${node.args.map(child).join(", ")})`
      default: throw new KpiFormulaError("syntax")
    }
  }
  const expression = write(tree)
  parseKpiFormula(expression)
  return expression
}

export function formulaFields(tree) {
  if (!tree) return []
  if (tree.type === "aggregate") return tree.column ? [tree] : []
  const children = tree.type === "binary" ? [tree.left, tree.right] : tree.type === "unary" ? [tree.operand] : tree.args || tree.values || []
  return children.flatMap(formulaFields)
}

export function formulaFieldAliases(names) {
  const result = new Map()
  for (const name of names) {
    let safe = name.replace(/\W+/g, "_").replace(/^_+|_+$/g, "")
    if (/^\d/.test(safe)) safe = `_${safe}`
    for (const alias of [name, name.toLowerCase(), safe, safe.toLowerCase()]) if (/^[A-Za-z_]\w*$/.test(alias) && !result.has(alias)) result.set(alias, name)
  }
  return result
}

export function formulaErrorText(error, labels) {
  const keys = { empty: "formulaParseEmpty", length: "formulaParseLength", field: "formulaParseField", number: "formulaParseNumber", aggregateRequired: "formulaParseAggregate", function: "formulaParseFunction", arguments: "formulaParseArguments", complexity: "formulaParseComplexity", multiline: "formulaParseMultiline", chainedComparison: "formulaParseComparison", syntax: "formulaParseSyntax" }
  return labels[keys[error?.code] || "formulaParseSyntax"]
}
